import { doctorBffApi, InvalidateArr } from '../doctorBffApi.slice';
import {
  CcamModifier,
  ConventionStatus,
  CreateInvoicePayload,
  HospitalProcedureAnesthesiaPricePerProcedureCode,
  InsurancePlan,
  Invoice,
  ProcedureReimbursementInfo
} from './billing.model';
import { ID, QueryFilters } from '@ianesthesie-kit/services';
import { BillingService } from '../billing/billing.service';
import { wrapError } from '../error';
import {
  PatientId,
  surgeryInfoApi,
  WithPatientId
} from '@/modules/surgicalEvent';
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { captureThrowOnDev, PickEndsWith } from '@/util';

export const billingApi = doctorBffApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    insurancePlans: builder.query<InsurancePlan[], QueryFilters>({
      providesTags: ['HospitalSpecificData'],
      keepUnusedDataFor: 30,
      queryFn: (params) =>
        BillingService.fetchInsurancePlans(params)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),
    reimbursementInfos: builder.query<
      ProcedureReimbursementInfo[],
      QueryFilters
    >({
      providesTags: ['HospitalSpecificData'],
      keepUnusedDataFor: 30,
      queryFn: (params) =>
        BillingService.fetchReimbursementInfos(params)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),
    hospitalAnesthesiaCosts: builder.query<
      HospitalProcedureAnesthesiaPricePerProcedureCode,
      QueryFilters | void | undefined
    >({
      providesTags: ['HospitalSpecificData'],
      keepUnusedDataFor: 30,
      queryFn: (params) =>
        BillingService.fetchHospitalAnesthesiaCosts(params ?? undefined)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),
    hospitalAnesthesiaCostsByCcam: builder.query<
      HospitalProcedureAnesthesiaPricePerProcedureCode,
      string[]
    >({
      providesTags: ['HospitalSpecificData'],
      keepUnusedDataFor: 30,
      queryFn: (ccamCodes) =>
        BillingService.fetchHospitalAnesthesiaCostsByCcam(ccamCodes)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),
    patientInvoices: builder.query<Invoice[], PatientId>({
      queryFn: ({ patientId }) =>
        BillingService.getInstance(patientId)
          .fetch()
          .then(({ data }) => ({ data }))
          .catch(wrapError),
      providesTags: (res, err, params) => {
        const arr: InvalidateArr = [];
        if (!err && res) {
          arr.push({
            type: 'Invoice',
            id: params.patientId
          });
        }

        return arr;
      }
    }),
    createPatientInvoice: builder.mutation<
      Invoice,
      WithPatientId<CreateInvoicePayload>
    >({
      queryFn: ({ patientId, ...payload }) =>
        BillingService.getInstance(patientId)
          .generate(payload)
          .then(({ data }) => ({ data }))
          .catch(wrapError),
      // Generating the devis upserts the acte billing line (amount, billing doctor) and records
      // an `ACTE_LINE_UPDATED` audit event, so the receivables views and the line's trail are
      // stale too.
      invalidatesTags: (res, err, arg) => {
        if (!res || err) {
          return [];
        }
        return [
          {
            type: 'Invoice',
            id: arg.patientId
          },
          'BillingLines',
          'BillingDashboard',
          'BillingSummary',
          'BillingLineEvents'
        ];
      }
    }),

    createFollowUpPlanInvoice: builder.mutation<
      Invoice,
      WithPatientId<CreateInvoicePayload & { planId: ID }>
    >({
      queryFn: ({ patientId, planId, ...payload }) =>
        BillingService.getInstance(patientId)
          .generateForPlan(planId, payload)
          .then(({ data }) => ({ data }))
          .catch(wrapError),
      // A plan quote has no billing line yet; only the invoice list changes.
      invalidatesTags: (res, err, arg) =>
        !res || err ? [] : [{ type: 'Invoice', id: arg.patientId }]
    }),

    sendPatientInvoice: builder.mutation<void, Invoice>({
      queryFn: (invoice) =>
        BillingService.getInstance(invoice.patientId)
          .sendToPatient(invoice)
          .then(() => ({ data: {} as any }))
          .catch(wrapError),
      // Sending is what arms the acte line's aging clock (the anchor is the invoice send date),
      // so the line list, the chase table, the summary strip and the line's audit trail
      // (`ACTE_LINE_UPDATED`) all change here.
      invalidatesTags: (res, err, arg) => {
        if (!res || err) {
          return [];
        }
        return [
          {
            type: 'Invoice',
            id: arg.patientId
          },
          'BillingLines',
          'BillingDashboard',
          'BillingSummary',
          'BillingLineEvents'
        ];
      }
    }),
    signPatientInvoiceOnSite: builder.mutation<void, Invoice>({
      queryFn: (invoice) =>
        BillingService.getInstance(invoice.patientId)
          .signOnSite(invoice.id)
          .then(() => ({ data: {} as any }))
          .catch(wrapError),
      // An on-site signature settles the devis the same way a send does — aging anchor,
      // receivables views and the line's `ACTE_LINE_UPDATED` trail entry.
      invalidatesTags: (res, err, arg) => {
        if (!res || err) {
          return [];
        }
        return [
          {
            type: 'Invoice',
            id: arg.patientId
          },
          'ProtocolStatuses',
          'BillingLines',
          'BillingDashboard',
          'BillingSummary',
          'BillingLineEvents'
        ];
      }
    }),
    modifiers: builder.query<CcamModifier[], void>({
      queryFn: () =>
        BillingService.fetchModifiers()
          .then((data) => ({ data }))
          .catch(wrapError)
    }),
    updateConventionStatus: builder.mutation<void, ConventionStatus>({
      // The consultation-line suggestion is priced off the doctor's convention sector
      // (`BillingLineService#resolveSector` in medical-synthesis), so a sector change must
      // refresh it — it provides the untargeted `BillingSettings` tag. The quote reads the
      // sector off the hospital's doctor list.
      invalidatesTags: (res, err) =>
        err ? [] : ['BillingSettings', { type: 'Doctor', id: 'LIST' }],
      queryFn: (status) =>
        BillingService.updateConventionStatus(status)
          .then(() => ({ data: {} as unknown as void }))
          .catch(wrapError)
    })
  })
});

export const {
  useInsurancePlansQuery: useInsurancePlans,
  useReimbursementInfosQuery: useReimbursementInfos,
  useHospitalAnesthesiaCostsQuery: useHospitalAnesthesiaCosts,
  useHospitalAnesthesiaCostsByCcamQuery: useHospitalAnesthesiaCostsByCcam,
  useLazyHospitalAnesthesiaCostsByCcamQuery:
    useLazyHospitalAnesthesiaCostsByCcam,
  usePatientInvoicesQuery,
  useCreatePatientInvoiceMutation,
  useCreateFollowUpPlanInvoiceMutation,
  useSendPatientInvoiceMutation,
  useSignPatientInvoiceOnSiteMutation,
  useModifiersQuery,
  useUpdateConventionStatusMutation
} = billingApi;

/** The convention sectors billed as OPTAM: sector 1 and the OPTAM signatories. */
const OPTAM_SECTORS: ReadonlySet<ConventionStatus> = new Set([
  'S1',
  'S2_OPTAM',
  'S2_OPTAM_ACO'
]);

export type InvoiceCalculationType =
  | 'surgeonBased'
  | 'insuranceBased'
  | 'brssBased'
  | 'anesthesiaBased';

/**
 * Which quote the builder edits: the event's own, or the one of a surgery planned at this
 * consultation whose event does not exist yet.
 */
export type InvoiceBuilderTarget =
  | { kind: 'event' }
  | { kind: 'plan'; planId: ID };

/** The billing doctor as the quote needs it: who issues it, and the sector it is priced with. */
export type BillingDoctor = {
  id: ID;
  conventionStatus?: ConventionStatus;
  patientId: ID;
  surgicalEventId: ID;
};

export type InvoiceBuilderState = {
  target: InvoiceBuilderTarget;
  /**
   * The billing doctor the quote is issued under, as the billing card resolves it; unset while
   * none can be.
   */
  billingDoctor?: BillingDoctor;
  /** The billing doctor picked for a consultation fee that is not confirmed yet. */
  pickedBillingDoctorId?: ID;
  optam?: boolean;
  procedureReimbursements: {
    id: number;
    info?: ProcedureReimbursementInfo;
    hospitalAnesthesiaPrice?: number;
    forceEligibleBrssRateToFull?: boolean;
    source?: 'ccam' | 'automation' | 'manual';
  }[];
  fixedConsultationFee?: number;
  insurancePlan: {
    planInput: 'predefined' | 'manualInput';
    plan?: InsurancePlan;
    manualPlan?: PickEndsWith<InsurancePlan, 'Optam'>;
  };
  invoiceCalculation: {
    selectedType?: InvoiceCalculationType;
    surgeonBased: {
      surgeonPrice?: number;
      percentage?: number;
      manualPrice?: number;
    };
    insuranceBased: {
      percentage?: number;
      manualPrice?: number;
    };
    brssBased: {
      base?: number;
      percentage?: number;
      manualPrice?: number;
    };
    anesthesiaBased: {
      price?: number;
    }[];
  };
  selectedModifiersByCode: Record<string, string[]>;
  /** Rows priced without a CCAM code; `amount` is unset until the doctor types one. */
  manualFees: {
    id: number;
    label: string;
    amount?: number;
  }[];
};

/** Cap on billing lines per devis, CCAM and manual fee lines combined. */
export const MAX_BILLING_LINES = 3;

/** Mirrors the backend cap on a manual fee label. */
export const MAX_MANUAL_FEE_LABEL_LENGTH = 250;

/** Counts CCAM and manual fee lines together, the way {@link MAX_BILLING_LINES} caps them. */
const countBillingLines = (state: InvoiceBuilderState) =>
  state.procedureReimbursements.length + state.manualFees.length;

const initialState: InvoiceBuilderState = {
  target: { kind: 'event' },
  procedureReimbursements: [],
  insurancePlan: {
    planInput: 'predefined'
  },
  invoiceCalculation: {
    surgeonBased: {},
    insuranceBased: {},
    brssBased: {},
    anesthesiaBased: []
  },
  selectedModifiersByCode: {},
  manualFees: []
};

const getBrssSortValue = (
  entry: InvoiceBuilderState['procedureReimbursements'][number],
  optam: boolean | undefined
): number => {
  const info = entry.info;
  if (!info) {
    return -Infinity;
  }
  const field = optam ? 'anesthesiaWithOptam' : 'anesthesiaWithoutOptam';
  return info[field] ?? -Infinity;
};

const reorderByRevenueOptimalBrss = (state: Draft<InvoiceBuilderState>) => {
  const prs = state.procedureReimbursements;
  const calcs = state.invoiceCalculation.anesthesiaBased;
  if (prs.length < 2) {
    return;
  }
  const optam = state.optam;
  const paired = prs.map((entry, i) => ({
    entry,
    calc: calcs[i],
    sortValue: getBrssSortValue(entry, optam)
  }));
  paired.sort((a, b) => b.sortValue - a.sortValue);
  const populatedCount = paired.filter((p) => p.sortValue > -Infinity).length;
  // For 3 populated entries, place smallest at index 1 (50% rate) and middle
  // at index 2 (75% rate) so the smallest BRSS absorbs the largest reduction.
  const permutation =
    populatedCount === 3 ? [paired[0], paired[2], paired[1]] : paired;
  state.procedureReimbursements = permutation.map((p) => p.entry);
  state.invoiceCalculation.anesthesiaBased = permutation.map((p) => p.calc);
};

export const invoiceBuilderSlice = createSlice({
  name: 'invoiceBuilder',
  initialState,
  reducers: {
    updateOptam: (state, action: PayloadAction<boolean | undefined>) => {
      state.optam = action.payload;
      reorderByRevenueOptimalBrss(state);
    },
    /**
     * Sets the billing doctor the quote is issued under. OPTAM follows the doctor's convention
     * sector, non-OPTAM without one; the toggles can still change it for this quote.
     */
    setBillingDoctor: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<BillingDoctor | undefined>
    ) => {
      const previous = state.billingDoctor;
      const changed =
        previous?.id !== action.payload?.id ||
        previous?.conventionStatus !== action.payload?.conventionStatus;
      if (
        !changed &&
        previous?.patientId === action.payload?.patientId &&
        previous?.surgicalEventId === action.payload?.surgicalEventId
      ) {
        return;
      }
      state.billingDoctor = action.payload;
      if (!changed) {
        return;
      }
      const sector = action.payload?.conventionStatus;
      state.optam = !!sector && OPTAM_SECTORS.has(sector);
      state.selectedModifiersByCode = {};
      state.invoiceCalculation.brssBased.base = undefined;
      reorderByRevenueOptimalBrss(state);
    },
    /** Picks the billing doctor of a consultation fee that is not confirmed yet. */
    pickBillingDoctor: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<ID>
    ) => {
      state.pickedBillingDoctorId = action.payload;
    },
    updateConsultationCapByAsa: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<number | undefined>
    ) => {
      const asa = action.payload || 1;
      // Fixed cap values for France: 31.5€ (ASA1 & 2) | 56.5€ (ASA3+)
      state.fixedConsultationFee = asa < 3 ? 31.5 : 56.5;
    },
    updateProcedureReimbursement: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<
        Partial<
          InvoiceBuilderState['procedureReimbursements'][number] & {
            index?: number;
          }
        >
      >
    ) => {
      if (
        action.payload.index === undefined &&
        action.payload.id === undefined
      ) {
        captureThrowOnDev(
          'Invalid action calling. Either id or index must be provided'
        );
        return;
      }
      const i =
        action.payload.index ??
        state.procedureReimbursements.findIndex(
          (pr) => pr.id === action.payload.id
        );
      if (i < 0) {
        return;
      }
      const existing = state.procedureReimbursements[i] ?? { id: Date.now() };
      state.procedureReimbursements[i] = {
        ...existing,
        ...action.payload
      };
      const currentProcedureReimbursement = state.procedureReimbursements[i];
      const procedureSelected = !!currentProcedureReimbursement.info;
      const optamExists = state.optam !== undefined;
      if (procedureSelected && !optamExists) {
        state.optam = false;
      }

      state.invoiceCalculation.anesthesiaBased[i] = {
        price: currentProcedureReimbursement.hospitalAnesthesiaPrice
      };
      reorderByRevenueOptimalBrss(state);
    },
    addProcedureReimbursement: (state: Draft<InvoiceBuilderState>) => {
      if (countBillingLines(state) >= MAX_BILLING_LINES) {
        return;
      }
      state.procedureReimbursements.push({
        id: Date.now()
      });
      state.invoiceCalculation.anesthesiaBased.push({});
    },
    removeProcedureReimbursementAtIndex: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<number>
    ) => {
      const index = action.payload;
      if (index >= 0) {
        const code = state.procedureReimbursements[index]?.info?.code;
        state.procedureReimbursements.splice(index, 1);
        state.invoiceCalculation.anesthesiaBased.splice(index, 1);
        if (code) {
          delete state.selectedModifiersByCode[code];
        }
        reorderByRevenueOptimalBrss(state);
      }
    },
    toggleForceEligibleBrssRateToFull: (
      state,
      action: PayloadAction<{ index: number }>
    ) => {
      const index = action.payload.index;
      if (index >= 0 && index < state.procedureReimbursements.length) {
        const procedureReimbursement = state.procedureReimbursements[index];
        if (procedureReimbursement) {
          procedureReimbursement.forceEligibleBrssRateToFull =
            !procedureReimbursement.forceEligibleBrssRateToFull;
        }
      }
    },
    updateInsurancePlanInputType: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<InvoiceBuilderState['insurancePlan']['planInput']>
    ) => {
      state.insurancePlan.planInput = action.payload;
    },

    updateInsuranceManualPlan: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<InvoiceBuilderState['insurancePlan']['manualPlan']>
    ) => {
      state.insurancePlan.manualPlan = {
        ...state.insurancePlan.manualPlan,
        ...action.payload
      };
    },

    updateInsurancePlan: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<Partial<InvoiceBuilderState['insurancePlan']>>
    ) => {
      state.insurancePlan = {
        ...state.insurancePlan,
        ...action.payload
      };
    },

    updateInvoiceCalculationType: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<InvoiceCalculationType>
    ) => {
      state.invoiceCalculation.selectedType = action.payload;
    },

    updateInvoiceCalculationSurgeonPrice: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<number | undefined>
    ) => {
      state.invoiceCalculation.surgeonBased.surgeonPrice = action.payload;
    },
    updateBrssBase: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<number | undefined>
    ) => {
      state.invoiceCalculation.brssBased.base = action.payload;
    },
    updateInvoicePercentage: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<number | undefined>
    ) => {
      const type = state.invoiceCalculation.selectedType;
      if (type === 'anesthesiaBased') {
        return;
      }
      if (type) {
        state.invoiceCalculation[type].percentage = action.payload;
        if (typeof action.payload === 'number') {
          state.invoiceCalculation[type].manualPrice = undefined;
        }
      }
    },
    updateInvoiceManualPrice: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<
        number | { index: number; price: number | undefined } | undefined
      >
    ) => {
      const type = state.invoiceCalculation.selectedType;
      if (!type) {
        return;
      }
      if (type === 'anesthesiaBased') {
        const index =
          typeof action.payload === 'object' ? action.payload.index : 0;
        const price =
          typeof action.payload === 'object'
            ? action.payload.price
            : action.payload;
        state.invoiceCalculation.anesthesiaBased[index] = {
          price
        };
      } else {
        if (typeof action.payload !== 'number') {
          captureThrowOnDev('Invalid action calling');
          return;
        }
        state.invoiceCalculation[type].manualPrice = action.payload;
        if (typeof action.payload === 'number') {
          state.invoiceCalculation[type].percentage = undefined;
        }
      }
    },

    syncSourcedProcedures: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<{
        source: 'ccam' | 'automation';
        items: {
          info: ProcedureReimbursementInfo;
          hospitalAnesthesiaPrice?: number;
        }[];
      }>
    ) => {
      const { source, items } = action.payload;

      // For 'automation' source: only fill empty slots, never displace
      if (source === 'automation') {
        // Remove existing automation entries first
        for (let i = state.procedureReimbursements.length - 1; i >= 0; i--) {
          if (state.procedureReimbursements[i].source === 'automation') {
            state.procedureReimbursements.splice(i, 1);
            state.invoiceCalculation.anesthesiaBased.splice(i, 1);
          }
        }
        // Add items only to free slots; manual fee lines take slots too
        for (const item of items) {
          if (countBillingLines(state) >= MAX_BILLING_LINES) {
            break;
          }
          state.procedureReimbursements.push({
            id: Date.now() + state.procedureReimbursements.length,
            info: item.info,
            hospitalAnesthesiaPrice: item.hospitalAnesthesiaPrice,
            source: 'automation'
          });
          state.invoiceCalculation.anesthesiaBased.push({
            price: item.hospitalAnesthesiaPrice
          });
        }
      }

      // For 'ccam' source: can displace 'automation' codes
      if (source === 'ccam') {
        // Remove existing ccam entries first
        for (let i = state.procedureReimbursements.length - 1; i >= 0; i--) {
          if (state.procedureReimbursements[i].source === 'ccam') {
            state.procedureReimbursements.splice(i, 1);
            state.invoiceCalculation.anesthesiaBased.splice(i, 1);
          }
        }
        // Upgrade existing automation entries that match ccam codes
        for (const item of items) {
          const existingIdx = state.procedureReimbursements.findIndex(
            (pr) =>
              pr.source === 'automation' && pr.info?.code === item.info.code
          );
          if (existingIdx >= 0) {
            state.procedureReimbursements[existingIdx] = {
              id: state.procedureReimbursements[existingIdx].id,
              info: item.info,
              hospitalAnesthesiaPrice: item.hospitalAnesthesiaPrice,
              source: 'ccam'
            };
            state.invoiceCalculation.anesthesiaBased[existingIdx] = {
              price: item.hospitalAnesthesiaPrice
            };
          }
        }
        // Add remaining ccam items
        const alreadyAdded = new Set(
          state.procedureReimbursements
            .filter((pr) => pr.source === 'ccam')
            .map((pr) => pr.info?.code)
        );
        for (const item of items) {
          if (alreadyAdded.has(item.info.code)) {
            continue;
          }
          if (countBillingLines(state) < MAX_BILLING_LINES) {
            state.procedureReimbursements.push({
              id: Date.now() + state.procedureReimbursements.length,
              info: item.info,
              hospitalAnesthesiaPrice: item.hospitalAnesthesiaPrice,
              source: 'ccam'
            });
            state.invoiceCalculation.anesthesiaBased.push({
              price: item.hospitalAnesthesiaPrice
            });
          } else {
            // Try to displace an automation code
            const automationIdx = state.procedureReimbursements.findIndex(
              (pr) => pr.source === 'automation'
            );
            if (automationIdx >= 0) {
              state.procedureReimbursements[automationIdx] = {
                id: state.procedureReimbursements[automationIdx].id,
                info: item.info,
                hospitalAnesthesiaPrice: item.hospitalAnesthesiaPrice,
                source: 'ccam'
              };
              state.invoiceCalculation.anesthesiaBased[automationIdx] = {
                price: item.hospitalAnesthesiaPrice
              };
            }
            // If no automation to displace, drop the excess code
          }
        }
      }

      if (
        state.procedureReimbursements.some((pr) => pr.info) &&
        state.optam === undefined
      ) {
        state.optam = false;
      }
      reorderByRevenueOptimalBrss(state);
    },

    /**
     * Adds a manual fee row. Each row stands for one of the quoted surgeries, so the Nth row's
     * label starts as the Nth surgery name (cut to the label limit), and is empty past the last.
     */
    addManualFee: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<string[]>
    ) => {
      if (countBillingLines(state) < MAX_BILLING_LINES) {
        const surgeryName = action.payload[state.manualFees.length] ?? '';
        state.manualFees.push({
          id: Date.now(),
          label: surgeryName.slice(0, MAX_MANUAL_FEE_LABEL_LENGTH)
        });
      }
    },
    updateManualFee: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<
        Pick<InvoiceBuilderState['manualFees'][number], 'id'> &
          Partial<Omit<InvoiceBuilderState['manualFees'][number], 'id'>>
      >
    ) => {
      const fee = state.manualFees.find((f) => f.id === action.payload.id);
      if (fee) {
        Object.assign(fee, action.payload);
      }
    },
    removeManualFee: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<number>
    ) => {
      state.manualFees = state.manualFees.filter(
        (f) => f.id !== action.payload
      );
    },
    // Keeps the target, which the card resets when the plan disappears, and the billing doctor
    // with its OPTAM, which the card sets again whenever its resolution changes.
    clearInvoiceBuilderState: (state: Draft<InvoiceBuilderState>) => {
      Object.assign(state, {
        ...initialState,
        pickedBillingDoctorId: undefined,
        billingDoctor: state.billingDoctor,
        optam: state.optam,
        target: state.target
      });
    },
    /**
     * Switches the quote being built. Patient-level inputs (billing doctor, OPTAM, consultation
     * fee, insurance plan) carry over; everything typed for the previous target's procedures is
     * dropped.
     */
    setInvoiceBuilderTarget: (
      state: Draft<InvoiceBuilderState>,
      action: PayloadAction<InvoiceBuilderTarget>
    ) => {
      Object.assign(state, {
        ...initialState,
        billingDoctor: state.billingDoctor,
        pickedBillingDoctorId: state.pickedBillingDoctorId,
        optam: state.optam,
        fixedConsultationFee: state.fixedConsultationFee,
        insurancePlan: state.insurancePlan,
        target: action.payload
      });
    },
    toggleModifier: (
      state,
      action: PayloadAction<{ code: string; modifierCode: string }>
    ) => {
      const { code, modifierCode } = action.payload;
      const current = state.selectedModifiersByCode[code] ?? [];
      const idx = current.indexOf(modifierCode);
      if (idx >= 0) {
        state.selectedModifiersByCode[code] = current.filter(
          (m) => m !== modifierCode
        );
      } else {
        state.selectedModifiersByCode[code] = [...current, modifierCode];
      }
    },
    initializeModifiersForCode: (
      state,
      action: PayloadAction<{
        code: string;
        conventionStatus?: ConventionStatus;
        modifiers: CcamModifier[];
      }>
    ) => {
      const { code, conventionStatus, modifiers } = action.payload;
      if (state.selectedModifiersByCode[code] !== undefined) {
        return;
      }
      const modifier3 = modifiers.find((m) => m.code === 'modifier_3');
      const autoOn =
        conventionStatus &&
        modifier3?.eligibleStatuses.includes(conventionStatus);
      state.selectedModifiersByCode[code] = autoOn ? ['modifier_3'] : [];
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      surgeryInfoApi.endpoints.clearExtractedCcamCodes.matchFulfilled,
      (state) => {
        const keep = state.procedureReimbursements.map(
          (pr) => pr.source !== 'ccam'
        );
        state.procedureReimbursements.forEach((pr, i) => {
          if (!keep[i] && pr.info?.code) {
            delete state.selectedModifiersByCode[pr.info.code];
          }
        });
        state.procedureReimbursements = state.procedureReimbursements.filter(
          (_, i) => keep[i]
        );
        state.invoiceCalculation.anesthesiaBased =
          state.invoiceCalculation.anesthesiaBased.filter((_, i) => keep[i]);
        reorderByRevenueOptimalBrss(state);
      }
    );
    builder.addMatcher(
      surgeryInfoApi.endpoints.removeExtractedCcamCode.matchFulfilled,
      (state, action) => {
        const removedCode = action.meta.arg.originalArgs.code;
        const idx = state.procedureReimbursements.findIndex(
          (pr) => pr.source === 'ccam' && pr.info?.code === removedCode
        );
        if (idx >= 0) {
          delete state.selectedModifiersByCode[removedCode];
          state.procedureReimbursements.splice(idx, 1);
          state.invoiceCalculation.anesthesiaBased.splice(idx, 1);
          reorderByRevenueOptimalBrss(state);
        }
      }
    );
  }
});

export const {
  updateOptam,
  setBillingDoctor,
  pickBillingDoctor,
  updateConsultationCapByAsa,
  updateProcedureReimbursement,
  addProcedureReimbursement,
  removeProcedureReimbursementAtIndex,
  toggleForceEligibleBrssRateToFull,
  updateInsurancePlan,
  updateInsuranceManualPlan,
  updateInsurancePlanInputType,
  updateInvoiceCalculationType,
  syncSourcedProcedures,
  clearInvoiceBuilderState,
  setInvoiceBuilderTarget,
  addManualFee,
  updateManualFee,
  removeManualFee,
  updateInvoiceCalculationSurgeonPrice,
  updateBrssBase,
  updateInvoicePercentage,
  updateInvoiceManualPrice,
  toggleModifier,
  initializeModifiersForCode
} = invoiceBuilderSlice.actions;

export const invoiceBuilderReducer = invoiceBuilderSlice.reducer;
